import { APP_CONFIG } from "../constants/global-variables.js";

const SPORTS_CACHE_KEY = "sports_cache_v1";
const SPORTS_CACHE_EXPIRY_KEY = "sports_cache_expiry_v1";

// de-dupe concurrent requests across the app
let inFlight = null;

/** Ensure we always return a clean array of sports */
function sanitizeSports(raw) {
  if (!Array.isArray(raw)) return [];
  // (optional) normalize records you rely on
  return raw
    .filter(Boolean)
    .map((s) => ({
      id: s?.id ?? null,
      name: s?.name ?? "",
      count_sport:
        typeof s?.count_sport === "number"
          ? s.count_sport
          : Number(s?.count_sport) || 0,
      ...s,
    }))
    .filter((s) => s.id !== null); // require an id
}

function readCache() {
  let cached = null;
  let expiry = 0;

  try {
    const cachedStr = localStorage.getItem(SPORTS_CACHE_KEY);
    if (cachedStr) cached = JSON.parse(cachedStr);
  } catch {
    // wipe bad cache
    localStorage.removeItem(SPORTS_CACHE_KEY);
    cached = null;
  }

  const expiryStr = localStorage.getItem(SPORTS_CACHE_EXPIRY_KEY);
  if (expiryStr && !Number.isNaN(Number(expiryStr))) {
    expiry = Number(expiryStr);
  }

  return { cached, expiry };
}

function writeCache(data, ttlMs) {
  try {
    const clean = sanitizeSports(data);
    localStorage.setItem(SPORTS_CACHE_KEY, JSON.stringify(clean));
    localStorage.setItem(SPORTS_CACHE_EXPIRY_KEY, String(Date.now() + ttlMs));
    return clean;
  } catch {
    // ignore quota errors
    return sanitizeSports(data);
  }
}

async function networkFetch(url) {
  const res = await axios.get(url);
  return sanitizeSports(res.data);
}

/**
 * Returns cached sports data immediately when available.
 * If cache is expired, triggers a background refresh and still returns cached data.
 * If no cache exists yet, fetches from network, stores it, and returns it.
 *
 * Always resolves to an Array. Never undefined.
 *
 * @param {object} opts
 * @param {number} opts.ttlMinutes - cache TTL in minutes (default 5)
 */
export async function getSports({ ttlMinutes = 5 } = {}) {
  const ttlMs = ttlMinutes * 60 * 1000;
  const url = `${APP_CONFIG.URL.apiBasePath}ajax/sports`;

  // Server-side: just fetch & sanitize
  if (typeof window === "undefined") {
    try {
      const data = await networkFetch(url);
      return data;
    } catch {
      return []; // never throw to caller, stay predictable
    }
  }

  const now = Date.now();
  let { cached, expiry } = readCache();

  // Normalize cache shape early
  cached = sanitizeSports(cached);

  const hasCache = cached.length > 0;
  const isFresh = now < expiry;

  if (hasCache && isFresh) {
    return cached; // ✅ fresh cache
  }

  // If expired cache exists, return it immediately and refresh in the background.
  if (hasCache && !isFresh) {
    void refreshSportsCache(url, ttlMs);
    return cached; // ✅ stale-but-usable cache
  }

  // No usable cache—dedupe concurrent fetches
  if (!inFlight) {
    inFlight = (async () => {
      try {
        const data = await networkFetch(url);
        return writeCache(data, ttlMs);
      } catch {
        // If network fails and we truly had no cache, return empty array
        return [];
      } finally {
        // release the in-flight slot on next tick to avoid race with .then users
        const tmp = inFlight;
        setTimeout(() => {
          if (inFlight === tmp) inFlight = null;
        }, 0);
      }
    })();
  }

  // Wait for the first fetch to settle; always an array
  return inFlight;
}

/**
 * Force a background refresh and update cache.
 * Dispatches a window event so UI can react if desired.
 */
async function refreshSportsCache(url, ttlMs) {
  try {
    const data = await networkFetch(url);
    const clean = writeCache(data, ttlMs);

    // Optional: let listeners know cache updated
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("sports-cache-updated", { detail: { data: clean } })
      );
    }
  } catch (err) {
    // Keep serving old cache on failures.
    console.warn("Failed to refresh sports cache:", err?.message || err);
  }
}
